import { startTransition, useState } from 'react';
import type { Selection } from 'react-aria-components';
import { Autocomplete } from '@gonfalon/launchpad-experimental/Autocomplete';
import { Button, Group, IconButton, Input, ListBox, ListBoxItem, SearchField } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';

import styles from './TagGroup.module.css';

interface TagItem {
  id: string;
  name: string;
  isNew?: boolean;
}

export interface DefaultTagAutocompleteProps {
  selectedKeys: Selection;
  onSelectionChange: (keys: Selection) => void;
  placeholder: string;
  tags: string[];
}

export function DefaultTagAutocomplete({
  selectedKeys,
  onSelectionChange,
  placeholder,
  tags,
}: DefaultTagAutocompleteProps) {
  const [filterText, setFilterText] = useState('');

  const existingItems: TagItem[] = tags.map((tag) => ({ id: tag, name: tag }));
  let items: TagItem[] = existingItems;

  if (filterText.trim() && !tags.includes(filterText.trim())) {
    items = [
      { id: filterText.trim(), name: filterText.trim(), isNew: true },
      ...existingItems.filter((item) => item.name.toLowerCase().includes(filterText.toLowerCase())),
    ];
  }

  const emptySelection = selectedKeys === 'all' ? false : selectedKeys.size === 0;

  return (
    <Autocomplete
      defaultInputValue={filterText}
      onInputChange={(nextFilter) => startTransition(() => setFilterText(nextFilter))}
    >
      <SearchField aria-label="Search tags…" autoFocus>
        <Group>
          <Icon name="search" size="small" />
          <Input placeholder={placeholder} />
          <IconButton aria-label="clear" icon="cancel-circle-outline" size="small" variant="minimal" />
        </Group>
      </SearchField>

      <ListBox
        className={styles.listbox}
        selectionMode="multiple"
        items={items}
        selectedKeys={selectedKeys}
        onSelectionChange={onSelectionChange}
        renderEmptyState={() => (
          <span className={styles.emptyState}>
            {filterText.trim() ? 'No matching tags' : 'Start typing to add tags'}
          </span>
        )}
      >
        {(item) => (
          <ListBoxItem id={item.id}>
            {item.isNew ? (
              <span className={styles.new}>
                Add: <i>{item.name}</i>
              </span>
            ) : (
              item.name
            )}
          </ListBoxItem>
        )}
      </ListBox>

      {!emptySelection && (
        <div className={styles.clearContainer}>
          <Button variant="minimal" size="small" onPress={() => onSelectionChange(new Set())}>
            Clear all
          </Button>
        </div>
      )}
    </Autocomplete>
  );
}
